/**
 * Per-clip shape MASK — shared by preview + export. Sets a clip path on the context BEFORE the
 * media frame is blitted, so only the masked region shows. Pure canvas (no shader/backend), works
 * CPU + GPU. Geometry is normalized 0..1 within the frame rect (fx,fy,fw,fh).
 *
 * v1 uses hard-edged `ctx.clip()` shapes (rectangle/ellipse/circle) + evenodd for invert. `linear`
 * clips to a half of the frame along `angle`. Feather is approximated by the caller (soft shadow)
 * or left hard for v1.
 */

import { applyEase, type KeyframeEase } from './enter-exit-presets';

type AnyCtx = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

export type MaskShape =
  | 'rectangle'
  | 'ellipse'
  | 'circle'
  | 'linear'
  | 'radial'
  | 'split'
  | 'filmstrip'
  | 'star'
  | 'heart'
  | 'text'
  | 'brush'
  | 'pen';

export interface MaskPoint { x: number; y: number; }

export interface MaskKeyframe {
  /** Seconds from the owning clip's start. */
  tSeconds: number;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  angle?: number;
  feather?: number;
  fillOpacity?: number;
  ease?: KeyframeEase;
}

export interface MaskParams {
  id?: string;
  shape: MaskShape;
  feather?: number;
  invert?: boolean;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  angle?: number;
  fillColor?: string | null;
  fillOpacity?: number;
  /** Text stencil content. Only used by the text shape. */
  text?: string;
  fontFamily?: string;
  fontWeight?: number;
  /** Freehand normalized points used by brush/pen masks. */
  points?: MaskPoint[];
  brushSize?: number;
  keyframes?: MaskKeyframe[] | null;
}

export type ResolvedMask = Omit<MaskParams, 'keyframes'> & {
  x: number;
  y: number;
  width: number;
  height: number;
  angle: number;
  feather: number;
  fillOpacity: number;
};

const MASK_EPSILON = 0.001;

/** Resolve one independently animated mask at a clip-relative time. */
export function resolveMaskAt(mask: MaskParams, clipRelativeSeconds: number): ResolvedMask {
  const base = resolvedBase(mask);
  const frames = sanitizeMaskKeyframes(mask.keyframes);
  if (!frames.length) return base;
  const time = round3(Math.max(0, clipRelativeSeconds));
  const exact = frames.find(frame => Math.abs(frame.tSeconds - time) <= MASK_EPSILON);
  if (exact) return mergeMaskFrame(base, exact);
  if (frames.length === 1 || time < frames[0]!.tSeconds) return mergeMaskFrame(base, frames[0]!);
  if (time > frames[frames.length - 1]!.tSeconds) return mergeMaskFrame(base, frames[frames.length - 1]!);
  const rightIndex = frames.findIndex(frame => frame.tSeconds > time);
  if (rightIndex <= 0) return base;
  const left = frames[rightIndex - 1]!;
  const right = frames[rightIndex]!;
  const span = Math.max(MASK_EPSILON, right.tSeconds - left.tSeconds);
  const progress = applyEase((time - left.tSeconds) / span, left.ease);
  const leftMask = mergeMaskFrame(base, left);
  const rightMask = mergeMaskFrame(base, right);
  return {
    ...base,
    x: lerp(leftMask.x, rightMask.x, progress),
    y: lerp(leftMask.y, rightMask.y, progress),
    width: lerp(leftMask.width, rightMask.width, progress),
    height: lerp(leftMask.height, rightMask.height, progress),
    angle: lerpAngle(leftMask.angle, rightMask.angle, progress),
    feather: lerp(leftMask.feather, rightMask.feather, progress),
    fillOpacity: lerp(leftMask.fillOpacity, rightMask.fillOpacity, progress),
  };
}

export function maskKeyframeTimes(mask: MaskParams | null | undefined): number[] {
  return sanitizeMaskKeyframes(mask?.keyframes).map(frame => frame.tSeconds);
}

export function hasMaskKeyframeAt(mask: MaskParams | null | undefined, tSeconds: number): boolean {
  return maskKeyframeTimes(mask).some(time => Math.abs(time - tSeconds) <= MASK_EPSILON);
}

export function upsertMaskKeyframe(mask: MaskParams, tSeconds: number, value: MaskParams, ease: KeyframeEase = 'linear'): MaskKeyframe[] {
  const time = round3(Math.max(0, tSeconds));
  const next: MaskKeyframe = {
    tSeconds: time,
    x: round3(value.x ?? .5),
    y: round3(value.y ?? .5),
    width: round3(value.width ?? .6),
    height: round3(value.height ?? .6),
    angle: round3(value.angle ?? 0),
    feather: round3(value.feather ?? 0),
    fillOpacity: round3(value.fillOpacity ?? 1),
    ease,
  };
  const frames = sanitizeMaskKeyframes(mask.keyframes).filter(frame => Math.abs(frame.tSeconds - time) > MASK_EPSILON);
  return [...frames, next].sort((left, right) => left.tSeconds - right.tSeconds);
}

export function removeMaskKeyframeAt(mask: MaskParams, tSeconds: number): MaskKeyframe[] | null {
  const frames = sanitizeMaskKeyframes(mask.keyframes).filter(frame => Math.abs(frame.tSeconds - tSeconds) > MASK_EPSILON);
  return frames.length ? frames : null;
}

export function setMaskKeyframeEaseAt(mask: MaskParams, tSeconds: number, ease: KeyframeEase): MaskKeyframe[] | null {
  const frames = sanitizeMaskKeyframes(mask.keyframes).map(frame =>
    Math.abs(frame.tSeconds - tSeconds) <= MASK_EPSILON ? { ...frame, ease } : frame);
  return frames.length ? frames : null;
}

/**
 * Trace + apply the mask clip path onto `ctx` over the frame rect. Caller must `ctx.save()` first
 * and `ctx.restore()` after drawing the frame. Returns true if a clip was applied.
 */
export function applyMaskClip(ctx: AnyCtx, m: MaskParams, fx: number, fy: number, fw: number, fh: number): boolean {
  const cx = fx + (m.x ?? 0.5) * fw;
  const cy = fy + (m.y ?? 0.5) * fh;
  const w = (m.width ?? 0.6) * fw;
  const h = (m.height ?? 0.6) * fh;

  ctx.beginPath();
  switch (m.shape) {
    case 'circle': {
      ctx.arc(cx, cy, Math.min(w, h) / 2, 0, Math.PI * 2);
      break;
    }
    case 'ellipse':
    case 'radial': {
      ctx.ellipse(cx, cy, w / 2, h / 2, 0, 0, Math.PI * 2);
      break;
    }
    case 'linear': {
      // Half-plane along `angle`: clip to the frame-sized rect rotated so one side is kept.
      const ang = ((m.angle ?? 0) * Math.PI) / 180;
      const dx = Math.cos(ang), dy = Math.sin(ang);
      const big = Math.hypot(fw, fh);
      // Rectangle covering the "kept" half, starting at the mask center line.
      const nx = -dy, ny = dx; // perpendicular
      ctx.moveTo(cx - nx * big, cy - ny * big);
      ctx.lineTo(cx + nx * big, cy + ny * big);
      ctx.lineTo(cx + nx * big + dx * big, cy + ny * big + dy * big);
      ctx.lineTo(cx - nx * big + dx * big, cy - ny * big + dy * big);
      ctx.closePath();
      break;
    }
    case 'rectangle':
    default:
      ctx.rect(cx - w / 2, cy - h / 2, w, h);
      break;
  }

  if (m.invert) {
    ctx.rect(fx, fy, fw, fh); // outer, evenodd → keep everything except the shape
    ctx.clip('evenodd');
  } else {
    ctx.clip();
  }
  return true;
}

/** Paint the combined alpha for one or more masks onto a transparent mask surface.
 * Normal masks union together; inverted masks subtract from that union. When every
 * mask is inverted, the frame starts fully visible and each stencil cuts a hole. */
export function paintMaskAlpha(ctx: AnyCtx, masks: readonly ResolvedMask[], fx: number, fy: number, fw: number, fh: number): void {
  if (!masks.length) return;
  const normal = masks.filter(mask => !mask.invert);
  const inverted = masks.filter(mask => !!mask.invert);
  ctx.save();
  if (!normal.length) {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(fx, fy, fw, fh);
  }
  for (const mask of normal) paintOneMask(ctx, mask, fx, fy, fw, fh, 'source-over');
  for (const mask of inverted) paintOneMask(ctx, mask, fx, fy, fw, fh, 'destination-out');
  ctx.restore();
}

function paintOneMask(ctx: AnyCtx, mask: ResolvedMask, fx: number, fy: number, fw: number, fh: number, operation: GlobalCompositeOperation): void {
  ctx.save();
  ctx.globalCompositeOperation = operation;
  const featherPx = Math.max(0, mask.feather) * Math.min(fw, fh) * .08;
  if (featherPx > .25) ctx.filter = `blur(${round3(featherPx)}px)`;
  ctx.fillStyle = '#ffffff';
  ctx.strokeStyle = '#ffffff';
  traceOrPaintMask(ctx, mask, fx, fy, fw, fh);
  ctx.restore();
}

/** Paint one stencil. Shape paths are filled; pen/brush paths are stroked; text uses
 * the browser/worker canvas text engine so typed masks match in preview and export. */
export function traceOrPaintMask(ctx: AnyCtx, mask: ResolvedMask, fx: number, fy: number, fw: number, fh: number): void {
  const cx = fx + mask.x * fw;
  const cy = fy + mask.y * fh;
  const w = Math.max(1, mask.width * fw);
  const h = Math.max(1, mask.height * fh);
  const angle = mask.angle * Math.PI / 180;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(angle);
  if (mask.shape === 'text') {
    const text = (mask.text || 'TEXT').slice(0, 80);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `${Math.max(100, Math.min(900, mask.fontWeight ?? 800))} ${Math.max(8, h)}px ${mask.fontFamily || 'Arial, sans-serif'}`;
    ctx.fillText(text, 0, 0, w);
    ctx.restore();
    return;
  }
  if (mask.shape === 'brush' || mask.shape === 'pen') {
    const points = mask.points ?? [];
    if (points.length > 1) {
      ctx.beginPath();
      points.forEach((point, index) => {
        const px = (point.x - .5) * w;
        const py = (point.y - .5) * h;
        if (index === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      });
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.lineWidth = Math.max(2, (mask.brushSize ?? (mask.shape === 'brush' ? .12 : .035)) * Math.min(w, h));
      ctx.stroke();
    }
    ctx.restore();
    return;
  }

  ctx.beginPath();
  traceLocalShape(ctx, mask.shape, w, h);
  ctx.fill();
  ctx.restore();
}

function traceLocalShape(ctx: AnyCtx, shape: MaskShape, w: number, h: number): void {
  switch (shape) {
    case 'circle': ctx.arc(0, 0, Math.min(w, h) / 2, 0, Math.PI * 2); break;
    case 'ellipse':
    case 'radial': ctx.ellipse(0, 0, w / 2, h / 2, 0, 0, Math.PI * 2); break;
    case 'linear': {
      const big = Math.hypot(w, h) * 2;
      // Angle describes the visible divider line itself. Keep the half-plane below it.
      ctx.rect(-big / 2, 0, big, big);
      break;
    }
    case 'split': {
      const gap = Math.max(2, h * .08);
      ctx.rect(-w / 2, -h / 2, w, h / 2 - gap / 2);
      ctx.rect(-w / 2, gap / 2, w, h / 2 - gap / 2);
      break;
    }
    case 'filmstrip': {
      const band = h * .2;
      ctx.rect(-w / 2, -h / 2, w, band);
      ctx.rect(-w / 2, h / 2 - band, w, band);
      ctx.rect(-w / 2, -h * .22, w, h * .44);
      break;
    }
    case 'star': {
      const outer = Math.min(w, h) / 2;
      const inner = outer * .44;
      for (let i = 0; i < 10; i++) {
        const radius = i % 2 === 0 ? outer : inner;
        const a = -Math.PI / 2 + i * Math.PI / 5;
        const x = Math.cos(a) * radius * (w / Math.min(w, h));
        const y = Math.sin(a) * radius * (h / Math.min(w, h));
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.closePath();
      break;
    }
    case 'heart': {
      ctx.moveTo(0, h * .46);
      ctx.bezierCurveTo(-w * .08, h * .35, -w * .5, h * .08, -w * .5, -h * .16);
      ctx.bezierCurveTo(-w * .5, -h * .43, -w * .16, -h * .5, 0, -h * .24);
      ctx.bezierCurveTo(w * .16, -h * .5, w * .5, -h * .43, w * .5, -h * .16);
      ctx.bezierCurveTo(w * .5, h * .08, w * .08, h * .35, 0, h * .46);
      ctx.closePath();
      break;
    }
    case 'rectangle':
    default: ctx.rect(-w / 2, -h / 2, w, h); break;
  }
}

/** Draw an optional matte in the part of the frame hidden by the mask. A missing colour is
 * deliberately transparent, allowing lower overlay/video tracks to remain visible. */
export function drawMaskMatte(ctx: AnyCtx, m: MaskParams, fx: number, fy: number, fw: number, fh: number): void {
  if (!m.fillColor || (m.fillOpacity ?? 1) <= 0) return;
  ctx.save();
  // Applying the opposite clipping mode gives exactly the region the source clip did not draw.
  applyMaskClip(ctx, { ...m, invert: !m.invert }, fx, fy, fw, fh);
  ctx.globalAlpha *= Math.max(0, Math.min(1, m.fillOpacity ?? 1));
  ctx.fillStyle = m.fillColor;
  ctx.fillRect(fx, fy, fw, fh);
  ctx.restore();
}

function sanitizeMaskKeyframes(value: readonly MaskKeyframe[] | null | undefined): MaskKeyframe[] {
  return (value ?? [])
    .filter(frame => frame && Number.isFinite(frame.tSeconds))
    .map(frame => ({ ...frame, tSeconds: round3(Math.max(0, frame.tSeconds)) }))
    .sort((left, right) => left.tSeconds - right.tSeconds);
}

function resolvedBase(mask: MaskParams): ResolvedMask {
  return {
    ...mask,
    x: clamp(mask.x ?? .5, 0, 1),
    y: clamp(mask.y ?? .5, 0, 1),
    width: clamp(mask.width ?? .6, .01, 2),
    height: clamp(mask.height ?? .6, .01, 2),
    angle: mask.angle ?? 0,
    feather: clamp(mask.feather ?? 0, 0, 1),
    fillOpacity: clamp(mask.fillOpacity ?? 1, 0, 1),
  };
}

function mergeMaskFrame(base: ResolvedMask, frame: MaskKeyframe): ResolvedMask {
  return {
    ...base,
    x: clamp(frame.x ?? base.x, 0, 1),
    y: clamp(frame.y ?? base.y, 0, 1),
    width: clamp(frame.width ?? base.width, .01, 2),
    height: clamp(frame.height ?? base.height, .01, 2),
    angle: frame.angle ?? base.angle,
    feather: clamp(frame.feather ?? base.feather, 0, 1),
    fillOpacity: clamp(frame.fillOpacity ?? base.fillOpacity, 0, 1),
  };
}

function lerp(left: number, right: number, progress: number): number { return left + (right - left) * progress; }
function lerpAngle(left: number, right: number, progress: number): number {
  const delta = ((((right - left) % 360) + 540) % 360) - 180;
  return left + delta * progress;
}
function clamp(value: number, min: number, max: number): number { return Math.min(max, Math.max(min, value)); }
function round3(value: number): number { return Math.round(value * 1000) / 1000; }
